import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import {
  Button,
  InfoPage,
  isLoggedIn,
  LogSentryException,
  ServiceError,
  Spinner,
} from "@shapr3d/frontend-lib";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import ModelViewerHeader from "../../components/Headers/ModelViewerHeader/ModelViewerHeader";
import ModelViewerOverlay from "../../components/ModelViewerOverlay/ModelViewerOverlay";
import { SyncRequestClient } from "../../stores/requestClient";
import { ServiceErrorCode } from "../../utils/httpUtils";
import useTracker from "../../utils/useTracker";
import NotFoundPage from "../NotFoundPage/NotFoundPage";

const DrawingViewer = React.lazy(() => import("../../components/DrawingViewer/DrawingViewer"));
const ModelViewer = React.lazy(() => import("../../components/ModelViewer/ModelViewer"));

import { CommentCarousel } from "../../components/CommentCarousel/CommentCarousel";
import { CommentSidebar } from "../../components/CommentSidebar/CommentSidebar";
import { GenericHeader } from "../../components/Headers/GenericHeader/GenericHeader";
import ModelViewerNavbar from "../../components/ModelViewerNavbar/ModelViewerNavbar";
import { useHandleInvitation } from "../../hooks/useHandleInvitation";
import { useWebExport, webExportQueryKey } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import { navigateToAuth } from "../../utils/authUtils";
import { TemporarySalesBanner } from "./TemporarySalesBanner";

import style from "./ModelViewerPage.module.css";

const modelFileQueryKey = "modelFile";

export default function ModelViewerPage() {
  const tracker = useTracker();
  const { viewLink = "", drawingId } = useParams();

  const { isError: isInvitationError } = useHandleInvitation();

  const modelViewerRef = useRef<ModelViewerElement>(null);
  const queryClient = useQueryClient();

  const { viewportType } = useWindowSize();

  const [isNavbarOpen, setIsNavbarOpen] = useState(viewportType !== ViewportType.Mobile);
  const handleNavbarClick = useCallback(
    (isOpen: boolean) => {
      setIsNavbarOpen(isOpen);
      const isOpenText = isOpen ? "open" : "close";
      tracker.trackButtonClick({
        elementId: `navbar-${isOpenText}-button`,
        action: isOpen ? ElementClickAction.NAVBAR_OPEN : ElementClickAction.NAVBAR_CLOSE,
      });
    },
    [tracker]
  );

  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isDrawingLoaded, setIsDrawingLoaded] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isLoggedIn());

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isSidebarOpen) {
      setIsNavbarOpen(false);
    }
  }, [viewportType, isSidebarOpen]);

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isNavbarOpen) {
      setIsSidebarOpen(false);
    }
  }, [viewportType, isNavbarOpen]);

  const onModelViewerLoadHandler = useCallback(() => {
    setIsModelLoaded(true);
  }, []);

  const {
    data: webExportData,
    isLoading: isWebExportLoading,
    isSuccess: isWebExportSuccess,
    error: webExportError,
  } = useWebExport();

  useEffect(() => {
    setIsDrawingLoaded(false);
    setIsDrawingViewerError(false);
    setDrawingsProgress(0);
  }, [drawingId]);

  const onDrawingViewerLoadHandler = useCallback(() => {
    setIsDrawingLoaded(true);
  }, []);

  const onDrawingLoadProgressHandler = useCallback((progress: number) => {
    setDrawingsProgress(progress);
  }, []);

  const onDrawingViewerErrorHandler = useCallback(() => {
    setIsDrawingViewerError(true);
  }, []);

  const [isModelViewerError, setIsModelViewerError] = useState(false);
  const onModelViewerErrorHandler = useCallback(() => {
    if (!isModelViewerError) {
      tracker.trackModelDisplayFailed({
        webExportId: webExportData?.webExport?.webExportId,
        viewLinkId: viewLink,
      });
      setIsModelViewerError(true);
    }
  }, [tracker, isModelViewerError, webExportData?.webExport?.webExportId, viewLink]);

  const onDownloadPdfHandler = useCallback(
    async (drawingId: string) => {
      const drawingToDownload = webExportData?.webExport.drawingsDownload?.find(
        (drawing) => drawing.drawingId === drawingId
      );
      if (drawingToDownload?.download.url) {
        const res = await SyncRequestClient.publicRequest({
          method: "GET",
          url: drawingToDownload?.download.url,
        });
        const blob = new Blob([await res.blob()]);
        const blobURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = `${drawingToDownload.title}.pdf`;
        link.click();
        window.URL.revokeObjectURL(blobURL);
      }
    },
    [webExportData]
  );

  const [isDrawingViewerError, setIsDrawingViewerError] = useState(false);
  const [isWebExportDownloadError, setIsWebExportDownloadError] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [processProgress, setProcessProgress] = useState(0);
  const [drawingsProgress, setDrawingsProgress] = useState(0);
  const totalProgress = drawingId
    ? drawingsProgress
    : Math.round(downloadProgress * 0.9 + processProgress * 0.1);
  const isLoaded = drawingId ? isDrawingLoaded : isModelLoaded;

  const [modelLocalURL, setModelLocalURL] = useState("");
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(modelLocalURL);
    };
  }, [modelLocalURL]);

  // This invalidate the query for the model file download URL and avoid have all models in the cache
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [modelFileQueryKey, webExportData?.webExport?.modelFileDownload?.url],
    });
  }, [queryClient, webExportData?.webExport?.modelFileDownload?.url]);

  const downloadStartTimeRef = useRef(0);
  const { isError: isModelDownloadError } = useQuery({
    queryKey: [modelFileQueryKey, webExportData?.webExport?.modelFileDownload?.url],
    queryFn: async () => {
      try {
        downloadStartTimeRef.current = performance.now();
        const response = await SyncRequestClient.publicRequest({
          method: "GET",
          url: `${webExportData?.webExport.modelFileDownload?.url}`,
        });
        const blob = await response.blob((progress) => {
          setDownloadProgress(progress);
        });

        tracker.trackModelDownloaded({
          durationMs: Math.round(performance.now() - downloadStartTimeRef.current),
          viewLinkId: viewLink,
          webExportId: webExportData?.webExport.webExportId,
        });

        const url = URL.createObjectURL(blob);
        setModelLocalURL(url);

        return url;
      } catch (error) {
        tracker.trackModelDownloadFailed({
          webExportId: webExportData?.webExport?.webExportId,
          viewLinkId: viewLink,
        });
        LogSentryException({ messagePrefix: "Model download error", error });
        throw error;
      }
    },
    enabled: Boolean(webExportData?.webExport?.modelFileDownload?.url),
  });

  useEffect(() => {
    if (webExportError && webExportError instanceof ServiceError) {
      if (webExportError.hasCode(ServiceErrorCode.Forbidden)) {
        return;
      }
      if (webExportError.hasCode(ServiceErrorCode.AuthRequired)) {
        tracker.trackViewLinkAccessDenied({ viewLinkId: viewLink, reason: "missing_permission" });
        if (!isLoggedIn()) {
          navigateToAuth();
        }
        return;
      }
      setIsWebExportDownloadError(true);
      if (!webExportError.hasCode(ServiceErrorCode.InvalidRequestQueryParameter)) {
        LogSentryException({ messagePrefix: "Web export download error", error: webExportError });
      }
    }
  }, [webExportError, tracker, viewLink]);

  const reload = useCallback(() => {
    setModelLocalURL("");
    setDownloadProgress(0);
    setProcessProgress(0);
    setIsModelViewerError(false);
    setIsModelLoaded(false);

    setIsDrawingViewerError(false);
    setIsDrawingLoaded(false);
    setDrawingsProgress(0);

    queryClient.invalidateQueries({ queryKey: webExportQueryKey });
  }, [queryClient]);

  const modelTitle = webExportData?.webExport?.title ?? "";

  const drawing = useMemo(
    () =>
      webExportData?.webExport?.drawingsDownload?.find?.(
        (drawing) => drawing.drawingId === drawingId
      ),
    [webExportData?.webExport?.drawingsDownload, drawingId]
  );
  const isDrawingsLayout = useMemo(
    () => Boolean(webExportData?.webExport?.drawingsDownload?.length),
    [webExportData?.webExport?.drawingsDownload]
  );

  const navigate = useNavigate();
  useEffect(() => {
    if (drawingId !== undefined && !drawing && isWebExportSuccess) {
      navigate(`/v/${viewLink}${window.location.search}${window.location.hash}`);
    }
  }, [drawingId, isWebExportSuccess, drawing, viewLink, navigate]);

  if (isWebExportDownloadError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <NotFoundPage />
      </div>
    );
  }

  if (
    webExportError instanceof ServiceError &&
    webExportError.hasCode(ServiceErrorCode.AuthRequired)
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <NotFoundPage />
      </div>
    );
  }

  if (isInvitationError) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <InfoPage
          variant="warning"
          title={
            <>
              Something went wrong,
              <br />
              please try again.
            </>
          }
          primaryCTA={
            <Button
              onClick={() => {
                window.location.replace(window.location.href);
              }}
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (
    webExportError instanceof ServiceError &&
    webExportError?.hasCode(ServiceErrorCode.Forbidden) &&
    isLoggedIn()
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <InfoPage
          variant="information"
          title="You don't have permission to open this design"
          description="You may try to log in with a different account."
          primaryCTA={
            <Button
              onClick={() => {
                navigateToAuth({ shouldRelogin: true });
              }}
            >
              Back to log in
            </Button>
          }
        />
      </div>
    );
  }

  if (
    webExportError instanceof ServiceError &&
    webExportError.hasCode(ServiceErrorCode.AuthRequired)
  ) {
    return (
      <div className={style.container}>
        <GenericHeader />
        <div className={style.modelViewerContainer}>
          <Spinner></Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <ModelViewerHeader
        hideARButton={isWebExportLoading}
        disableARButton={!isModelLoaded}
        modelViewer={modelViewerRef}
        title={modelTitle}
        isDrawingsLayout={isDrawingsLayout}
        downloadPdf={onDownloadPdfHandler}
        teamId={webExportData?.teamId}
      />
      <div className={style.modelViewerContainer}>
        {!isLoaded && (
          <ModelViewerOverlay
            progress={totalProgress}
            error={
              (drawingId === undefined && (isModelDownloadError || isModelViewerError)) ||
              (drawingId !== undefined && isDrawingViewerError)
            }
            onRetry={reload}
          />
        )}

        <ModelViewerNavbar
          open={isNavbarOpen && isDrawingsLayout}
          setOpen={handleNavbarClick}
          webExport={webExportData?.webExport}
          isDrawingsLayout={isDrawingsLayout}
          isInDrawing={Boolean(drawingId)}
          isCommentingEnabled={Boolean(webExportData?.isCommentingEnabled)}
        />

        {(!drawingId || !isDrawingsLayout) && (
          <>
            <Suspense>
              <ModelViewer
                ref={modelViewerRef}
                src={modelLocalURL}
                modelDownloadURL={webExportData?.webExport?.modelFileDownload?.url}
                onLoad={onModelViewerLoadHandler}
                onError={onModelViewerErrorHandler}
                onLoadProgress={(progress) => setProcessProgress(progress)}
                isCommentingEnabled={webExportData?.isCommentingEnabled}
              />
            </Suspense>

            {viewportType !== ViewportType.Mobile &&
              (webExportData?.isCommentingEnabled || !isLoggedIn()) && (
                <CommentSidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
              )}
            {viewportType === ViewportType.Mobile && webExportData?.isCommentingEnabled && (
              <CommentCarousel />
            )}
          </>
        )}
        {drawingId && isDrawingsLayout && drawing && (
          <Suspense>
            <DrawingViewer
              key={drawing.download.url}
              drawing={drawing}
              onLoad={onDrawingViewerLoadHandler}
              onLoadProgress={onDrawingLoadProgressHandler}
              onError={onDrawingViewerErrorHandler}
            ></DrawingViewer>
          </Suspense>
        )}
        <TemporarySalesBanner />
      </div>
    </div>
  );
}
